(() => {
  if (customElements.get('playtime-first-order')) return;
  class FirstOrder extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
      const details = this.querySelector('details'), trigger = details?.querySelector('summary'), panel = this.querySelector('[data-offer-panel]');
      if (!trigger || !panel) return;
      this.lifecycle = new AbortController();
      const options = { signal:this.lifecycle.signal };
      const dialog = this.dialog = document.createElement('dialog');
      dialog.className = 'pt-offer__dialog';
      const title = panel.querySelector('h2');
      dialog.id = title.id + '-dialog';
      dialog.setAttribute('aria-labelledby',title.id);
      dialog.append(panel);
      this.append(dialog);
      details.open = false;
      trigger.setAttribute('aria-haspopup','dialog');
      trigger.setAttribute('aria-controls',dialog.id);
      trigger.setAttribute('aria-expanded','false');
      const close = panel.querySelector('[data-offer-close]');
      close.hidden = false;
      const form = panel.querySelector('form');
      const submit = panel.querySelector('[data-offer-submit]');
      const submitText = submit?.textContent;
      const accepted = panel.querySelector('[data-offer-accepted]');
      const copy = panel.querySelector('[data-offer-copy]');
      const code = accepted?.querySelector('[data-offer-code]');
      const reward = accepted?.querySelector('[data-offer-reward]');
      const status = accepted?.querySelector('[data-offer-copy-status]');
      const page = new URL(location.href);
      const returned = page.searchParams.get('pt_offer')==='return';
      if (returned) {
        page.searchParams.delete('pt_offer');
        try { window.history.replaceState(window.history.state,'',page); } catch {}
      }
      const burst = !!accepted && !matchMedia('(prefers-reduced-motion:reduce)').matches;
      if (burst) reward.hidden = true;
      const open = () => {
        if (dialog.open) return;
        dialog.showModal();
        trigger.setAttribute('aria-expanded','true');
        (copy && !reward.hidden ? copy : form.querySelector('input[type=email]') || close).focus({preventScroll:true});
      };
      trigger.addEventListener('click',e=>{ e.preventDefault(); open(); },options);
      close.addEventListener('click',()=>dialog.close(),options);
      dialog.addEventListener('close',()=>{
        this.clearConfetti();
        trigger.setAttribute('aria-expanded','false');
        if (this.isConnected) trigger.focus({preventScroll:true});
      },options);
      dialog.addEventListener('click',e=>{
        if (e.target !== dialog) return;
        const box=dialog.getBoundingClientRect();
        if (e.clientX<box.left || e.clientX>box.right || e.clientY<box.top || e.clientY>box.bottom) dialog.close();
      },options);
      if (submit) {
        const enable = () => { submit.disabled=false; submit.textContent=submitText; form.removeAttribute('aria-busy'); };
        form.addEventListener('submit',event=>{
          if (event.defaultPrevented) return;
          if (submit.disabled) { event.preventDefault(); return; }
          if (!form.checkValidity()) { event.preventDefault(); form.reportValidity(); return; }
          submit.disabled=true; submit.textContent=submit.dataset.sending; form.setAttribute('aria-busy','true');
          queueMicrotask(()=>{ if (event.defaultPrevented) enable(); });
        },options);
        window.addEventListener('pageshow',enable,options);
      }
      if (copy) {
        copy.hidden = false;
        code.addEventListener('click',()=>code.select(),options);
        copy.addEventListener('click',async ()=>{
          try {
            await navigator.clipboard.writeText(code.value);
            status.textContent=status.dataset.copied;
          } catch {
            code.focus({preventScroll:true}); code.select();
            status.textContent=status.dataset.manual;
          }
        },options);
      }
      const receiptUnknown = returned && !accepted && panel.querySelector('[data-offer-unconfirmed]');
      if (receiptUnknown) receiptUnknown.hidden=false;
      if (accepted || panel.querySelector('[data-offer-error]') || receiptUnknown) {
        open();
        if (!accepted) return;
        const reveal = () => {
          reward.hidden=false;
          status.textContent=status.dataset.ready;
          if (dialog.open && (document.activeElement===close || document.activeElement===dialog)) copy.focus({preventScroll:true});
        };
        if (burst) {
          this.celebrate(panel);
          this.revealTimer=setTimeout(reveal,210);
        } else reveal();
      }
    }
    celebrate(panel) {
      this.clearConfetti();
      if (matchMedia('(prefers-reduced-motion:reduce)').matches) return;
      const burst=document.createElement('div'); burst.className='pt-offer__confetti'; burst.setAttribute('aria-hidden','true');
      const box=panel.getBoundingClientRect(), width=window.innerWidth, height=window.innerHeight;
      const launchY=Math.min(height-24,box.bottom-60);
      for (const side of ['left','right']) {
        const dir=side==='left'?1:-1;
        const launchX=side==='left'?Math.max(16,box.left+8):Math.min(width-16,box.right-8);
        for (let i=0;i<120;i++) {
          const bit=document.createElement('i');bit.dataset.side=side;
          const spread=((i*73)%127)/126, rise=((i*47)%113)/112;
          const distance=dir*width*(-.42+spread*1.25);
          const apex=-Math.min(launchY+80,Math.max(height*.42,launchY)*(.65+rise*.5));
          const spin=dir*(260+((i*137)%940)), landing=height-launchY+80+((i*29)%120);
          const vars={'--flake':i%2?'#ECA8BC':'#D9F569','--launch-x':launchX+'px','--launch-y':launchY+'px',
            '--flake-width':(i%13===0?4:6+(i%6))+'px','--flake-height':(i%13===0?26:8+((i*7)%10))+'px',
            '--delay':(Math.floor(i/40)*90+(i%40)*2)+'ms','--duration':(1700+((i*37)%1000))+'ms',
            '--early-x':distance*.32+'px','--early-y':apex*.79+'px','--mid-x':distance*.62+'px','--late-x':distance*.87+'px',
            '--end-x':distance+'px','--apex-y':apex+'px','--late-y':apex*.28+'px','--land-y':landing+'px',
            '--early-spin':spin*.2+'deg','--mid-spin':spin*.46+'deg','--spin':spin+'deg','--end-spin':spin*1.5+'deg'};
          for (const [key,value] of Object.entries(vars)) bit.style.setProperty(key,value);
          burst.append(bit);
        }
      }
      this.confetti=burst;
      this.dialog.append(burst);
      if (typeof burst.showPopover==='function') {
        burst.setAttribute('popover','manual');
        burst.showPopover();
      }
      this.confettiTimer=setTimeout(()=>this.clearConfetti(),3200);
    }
    clearConfetti() {
      clearTimeout(this.confettiTimer);
      this.confetti?.remove();
      this.confetti=null;
    }
    disconnectedCallback() {
      if (!this.lifecycle) return;
      this.lifecycle.abort(); this.lifecycle=null;
      clearTimeout(this.revealTimer);
      this.clearConfetti();
      const panel=this.dialog?.querySelector('[data-offer-panel]');
      if (panel) this.querySelector('details').append(panel);
      this.dialog?.remove(); this.dialog=null;
    }
  }
  customElements.define('playtime-first-order',FirstOrder);
})();
